import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { Magnetic } from '@/components/generals/magnetic'
import { QuoteLink } from '@/components/generals/quote-link'
import { MEDIA } from '@/constants/media'

const SLICES = 5
const SLICE_WIDTH = 100 / SLICES + 0.2

/** Chapter nine: "in" flips to "on" while the photo assembles from vertical strips. */
export const ReadySection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            const title = q('[data-title]')[0]

            gsap.timeline({ scrollTrigger: { trigger: title, start: 'top 80%', toggleActions: 'play none none reverse' } })
                .fromTo(q('[data-title-line]'), { yPercent: 110 }, { yPercent: 0, duration: 1.2, stagger: 0.09, ease: 'expo.out' }, 0)

            gsap.set(q('[data-flip]'), { clearProps: 'transform' })

            gsap.fromTo(q('[data-flip]'), { yPercent: 0 }, {
                yPercent: -50,
                ease: 'power2.inOut',
                scrollTrigger: { trigger: title, start: 'top 55%', end: 'top 25%', scrub: 0.6 }
            })

            gsap.fromTo(q('[data-slice]'), {
                yPercent: (index: number) => (index % 2 === 0 ? 100 : -100)
            }, {
                yPercent: 0,
                ease: 'power3.out',
                stagger: 0.06,
                scrollTrigger: { trigger: q('[data-slices]')[0], start: 'top 90%', end: 'center 55%', scrub: 0.7 }
            })

            gsap.fromTo(q('[data-slices] img'), { scale: 1.25 }, {
                scale: 1,
                ease: 'none',
                scrollTrigger: { trigger: q('[data-slices]')[0], start: 'top bottom', end: 'bottom top', scrub: true }
            })

            gsap.fromTo(q('[data-ready-copy]'), { autoAlpha: 0, y: 30 }, {
                autoAlpha: 1,
                y: 0,
                duration: 1,
                stagger: 0.12,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-ready-copy]')[0], start: 'top 88%', toggleActions: 'play none none reverse' }
            })
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='ready'
            data-tone='ink'
            data-surface='dark'
            aria-labelledby='ready-title'
            className='relative bg-ink px-(--gutter) pb-[16vh] pt-[18vh] text-paper'
        >
            <div className='grid items-center gap-x-10 gap-y-16 lg:grid-cols-12'>
                <div className='lg:col-span-7'>
                    <p className='label text-haze'>Are you ready to&hellip;</p>

                    <h2
                        id='ready-title'
                        data-title=''
                        aria-label="Work on your business, not just in it."
                        className='display mt-8 text-[clamp(3rem,7.6vw,9.25rem)] leading-[0.88] wdth-75'
                    >
                        <span className='block'>
                            <span className='line-mask'>
                                <span data-title-line=''>
                                    Work{' '}
                                    <span className='relative inline-grid h-[0.86em] overflow-hidden align-top leading-[0.86]'>
                                        <span data-flip='' className='flex flex-col text-signal' style={{ transform: 'translateY(-50%)' }}>
                                            <span className='block h-[0.86em] text-haze'>in</span>
                                            <span className='block h-[0.86em]'>on</span>
                                        </span>
                                    </span>{' '}
                                    your
                                </span>
                            </span>
                        </span>

                        <span className='block'>
                            <span className='line-mask'>
                                <span data-title-line=''>business,</span>
                            </span>
                        </span>

                        <span className='block text-haze'>
                            <span className='line-mask'>
                                <span data-title-line=''>not just in it.</span>
                            </span>
                        </span>
                    </h2>

                    <div className='mt-12 grid gap-10 sm:grid-cols-[1fr_auto] sm:items-end'>
                        <p data-ready-copy='' className='max-w-[38ch] text-lg leading-relaxed text-haze md:text-xl'>
                            Let Localboost help your business grow, so that you can spend your time leading it into a better future for you and your team.
                        </p>

                        <div data-ready-copy=''>
                            <Magnetic strength={0.35}>
                                <QuoteLink
                                    data-cursor="Let's talk"
                                    className='group grid size-36 place-items-center rounded-full bg-signal text-center text-ink transition-colors duration-300 hover:bg-paper md:size-44'
                                >
                                    <span className='flex flex-col items-center gap-2'>
                                        <span className='text-lg font-semibold leading-none md:text-xl'>Free quote</span>
                                        <ArrowRight aria-hidden='true' className='size-5 -rotate-45 transition-transform duration-300 group-hover:rotate-0' />
                                    </span>
                                </QuoteLink>
                            </Magnetic>
                        </div>
                    </div>
                </div>

                <div className='lg:col-span-5'>
                    <div
                        data-slices=''
                        role='img'
                        aria-label={MEDIA.highFive.alt}
                        className='relative mx-auto aspect-[4/5] w-full max-w-[34rem] overflow-hidden bg-ink-soft'
                    >
                        {Array.from({ length: SLICES }, (_, index) => (
                            <div
                                key={index}
                                data-slice=''
                                className='absolute inset-y-0 overflow-hidden'
                                style={{ left: `${(index * 100) / SLICES}%`, width: `${SLICE_WIDTH}%` }}
                            >
                                <img
                                    src={MEDIA.highFive.src}
                                    alt=''
                                    loading='lazy'
                                    decoding='async'
                                    className='absolute inset-y-0 h-full max-w-none object-cover'
                                    style={{
                                        left: `${-((index * 100) / SLICES / SLICE_WIDTH) * 100}%`,
                                        width: `${10000 / SLICE_WIDTH}%`,
                                        objectPosition: '45% 50%'
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
